import React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import CardImage from "../../../card/CardImage";
import Dimensions from "../../../../views/inventory/requests/Dimensions";
import DetailRow from "../../../card/DetailRow";

export default function LocationSnapshotMobileView({
  collection,
  locationName,
  renderDimensions,
  getSubQty,
  onOpenAsset,
  auditModeEnabled,
  selectedAssetIdsSet,
  allVisibleSelected,
  someVisibleSelected,
  onToggleSelectAll,
  onToggleAssetSelection,
  onEditQuantity,
  onOpenLabelModal,
  isSavingQuantity,
  isRemoving,
}) {
  return (
    <Box mt={2} display="flex" flexDirection="column" gap={2}>
      {auditModeEnabled && (
        <Box display="flex" alignItems="center">
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected}
            onChange={event => onToggleSelectAll?.(event.target.checked)}
            disabled={isRemoving}
            inputProps={{ "aria-label": "select-all-mobile-assets" }}
          />
          <Typography fontWeight={500}>Select all</Typography>
        </Box>
      )}
      {collection.map(asset => {
        const rackLocations = Array.isArray(asset.rackLocations) ? asset.rackLocations : [];
        // With nested format, each location is a separate entry
        // Find other locations for this asset (excluding current one)
        const sharedLocations = rackLocations
          .flatMap(rl => (rl.warehouse_locations || []).map(loc => loc.location_name))
          .filter(name => name !== locationName)
          .filter(Boolean);

        return (
          <Card key={asset.id} id={`snapshot-${asset.id}`}>
            <CardContent>
              <Box display="flex" gap={1} alignItems="flex-start">
                {auditModeEnabled && (
                  <Checkbox
                    checked={selectedAssetIdsSet?.has(asset.id)}
                    onClick={event => onToggleAssetSelection?.(asset.id, event)}
                    disabled={isRemoving}
                    inputProps={{ "aria-label": `select-mobile-${asset.itemId || asset.id}` }}
                    sx={{ mt: -0.5 }}
                  />
                )}
                <Box flex={1}>
                  <CardImage image={asset.photo} />
                  <Box display="flex" flexDirection="column" gap={1}>
                    <DetailRow label="Item ID">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Box
                          sx={{ cursor: "pointer" }}
                          color="primary.main"
                          className="unfocus"
                          onClick={() => onOpenAsset?.(asset.id)}
                        >
                          {asset.itemId}
                        </Box>
                        <Tooltip title="Generate Label PDF" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onOpenLabelModal?.(asset)}
                            aria-label={`pdf-${asset.itemId || asset.id}`}
                          >
                            <PictureAsPdfIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </DetailRow>
                    {asset.model && <DetailRow label="Model" value={asset.model} />}
                    {renderDimensions &&
                      Array.isArray(asset.dimensions) &&
                      asset.dimensions.length > 0 &&
                      asset.dimensions.some(dim => Object.keys(dim).length > 0) && (
                        <DetailRow label="Dimensions (W x D x H)">
                          <Dimensions dimensions={asset.dimensions} />
                        </DetailRow>
                      )}
                    <DetailRow label="Total Quantity">
                      <Box>
                        {auditModeEnabled ? (
                          <TextField
                            disabled
                            value={getSubQty(rackLocations)}
                            size="small"
                            sx={{ maxWidth: 110 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Edit Location Quantity" placement="top">
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      onClick={() => onEditQuantity?.(asset)}
                                      disabled={isSavingQuantity || isRemoving}
                                      aria-label={`edit-qty-${asset.itemId || asset.id}`}
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {getSubQty(rackLocations)}
                          </Typography>
                        )}
                        {sharedLocations.length > 0 && (
                          <Typography variant="caption" color="error.main" display="block" mt={0.5}>
                            This quantity is split with: {sharedLocations.join(", ")}
                          </Typography>
                        )}
                      </Box>
                    </DetailRow>
                    <DetailRow label="Description" value={asset.description} />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
